import Honeybadger from "@honeybadger-io/js";
import { useRouter } from "next/router";

export interface HoneyBadgerErrorArgs {
  message: string;
  error?: any;
  email?: string;
  code?: string;
  cause?: unknown;
}

/**
 * The one-time-password worker returns `error` as a plain string when the
 * accounts service rejects the code, and as a `{ type, message }` object when
 * Cognito rejects it. Flatten either shape into a readable string so it never
 * reaches Honeybadger (or the error page) as "[object Object]".
 *
 * @param {unknown} error - The `error` field from the worker's JSON response.
 *
 * @returns {string} - A readable description of the error.
 */
export function formatOTPError(error: unknown): string {
  if (!error) {
    return "Unknown error";
  }

  if (typeof error === "string") {
    return error;
  }

  const { type, message } = error as { type?: string; message?: string };

  if (type && message) {
    return `${type}: ${message}`;
  }

  return message || type || JSON.stringify(error);
}

/**
 * Normalizes a cause into something Honeybadger can serialize. Its `sanitize`
 * helper walks enumerable properties, and an `Error`'s `message`/`stack` are
 * non-enumerable, so an Error cause would arrive as an empty object.
 */
function serializableCause(cause: unknown) {
  if (cause instanceof Error) {
    return { name: cause.name, message: cause.message, stack: cause.stack };
  }

  return cause;
}

/**
 * Logs an error to Honey Badger with the "Auth" category.
 *
 * @param {HoneyBadgerErrorArgs} args - The error message and optional error object.
 */
export function logHoneyBadgerError(args: HoneyBadgerErrorArgs) {
  const { message, error, email, code, cause = error?.cause } = args;
  const context = {
    category: "Auth",
    query: window.location.search,
    userAgent: navigator.userAgent,
    email,
    otpCode: code,
    cause: serializableCause(cause),
  };

  // Honeybadger drops null/undefined context keys, so callers with no `error`
  // object still need the message carried on the notice itself.
  Honeybadger.notify(error ?? message, { context, message });
}

/**
 * Sanitizes a given URI by ensuring it is either a valid relative path or a safe absolute URL.
 * This function performs a series of checks to validate the URI. If the URI is not provided,
 * invalid, or fails any of the checks, a default redirect URI ('/') is returned instead.
 *
 * @param {string} [uri] - The URI to be sanitized. Optional; if not provided, the default
 *                         redirect URI is used.
 *
 * @returns {string} - The sanitized URI if valid. If the URI is invalid, not provided, or
 *                     fails the checks (invalid protocol, hostname different from the current
 *                     window's hostname), the default redirect URI ('/') is returned.
 *
 * @example
 * // Returns '/path'
 * sanitizeUri('/path');
 *
 * @example
 * // Returns 'http://example.com' if the current hostname is 'example.com'
 * sanitizeUri('http://example.com');
 *
 * @example
 * // Returns '/' for an invalid URL
 * sanitizeUri('ftp://example.com');
 *
 */
export function sanitizeUri(uri?: string): string {
  const defaultRedirectUri = "/";

  if (!uri) {
    return defaultRedirectUri;
  }

  if (uri.startsWith("/")) {
    return uri;
  }

  try {
    const url = new URL(uri);
    if (url.protocol !== "http:" && url.protocol !== "https:") {
      logHoneyBadgerError({
        message: `Invalid protocol in sanitizeUri: ${url.protocol}`,
      });
      return defaultRedirectUri;
    } else if (url.hostname !== window.location.hostname) {
      logHoneyBadgerError({
        message: `Invalid hostname in sanitizeUri: ${url.hostname}`,
      });
      return defaultRedirectUri;
    }
    return uri;
  } catch (error) {
    logHoneyBadgerError({
      message: `Invalid URL in sanitizeUri: ${uri}`,
      error,
    });
    return defaultRedirectUri;
  }
}

/**
 * Hook that returns an OTP auth error handler. Intercepts "Incorrect username or
 * password" errors with an inline error state; logs and redirects all others.
 */
export function useOTPError() {
  const router = useRouter();

  return ({
    error,
    email,
    code,
    setOtpError,
  }: {
    error: Error;
    email: string;
    code: string;
    setOtpError: (msg: string) => void;
  }) => {
    const error403 = "Error: 403 ";
    if (String(error)?.includes(error403)) {
      setOtpError(String(error).replace(error403, ""));
    } else {
      logHoneyBadgerError({
        message: "There was an error attempting to log the user in.",
        error,
        email,
        code,
      });
      const errorUri = sanitizeUri(`/error?login-error`);
      // `encodeURI` leaves `&`, `=` and `#` intact, which would split the
      // message across query params on the error page.
      const errorDetail = error?.message ?? String(error);
      router.push(`${errorUri}=${encodeURIComponent(errorDetail)}`);
    }
  };
}
